import { Chip, IconButton, Stack } from '@mui/material';
import { useState, Fragment, useMemo } from 'react';
import { Close } from '@mui/icons-material';
import {
    Button,
    CloseIcon,
    DeleteIcon,
    Drawer,
    RightIcon,
    SaveIcon,
    Typography
} from '@uy3/web-components';
import { iconSx } from 'contexts/apiRequestContext';
import { activeTheme } from 'services/theme';
import { useFormContext } from 'contexts/formContext';
import { SelectFormField, TextFormField } from 'components/Forms/FormFields';
import {
    complianceRulesTypesList,
    getComplianceRuleByDescription,
    getComplianceRuleDescription,
    IComplianceRules
} from 'services/creditProduct';
import { useEconomicActivityCodeListData } from 'contexts/economicActivityCode';
import AutocompleteField from 'components/Forms/FormFields/AutocompleteField/AutocompleteField';

type ComplianceRulesFormContainerProps = {
    onClose: () => void;
};

const theme = activeTheme();

const CNAE_GRID_STYLE = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 2
};

const CnaeChipsGrid = ({
    cnaes,
    listOptions,
    onDelete,
    max = undefined
}: {
    cnaes: string[];
    listOptions: any[];
    onDelete: (code: string) => void;
    max?: number;
}) => {
    const chips = max ? cnaes.slice(0, max) : cnaes;
    return (
        <Stack sx={CNAE_GRID_STYLE}>
            {chips?.map((cnae) => {
                const found = listOptions.find((opt: any) => opt.code === cnae);
                const chipData = found || { label: cnae, value: cnae, code: cnae };
                return (
                    <Chip
                        key={chipData.value}
                        label={chipData.code}
                        deleteIcon={<Close sx={{ color: 'black' }} />}
                        onDelete={() => onDelete(cnae)}
                        sx={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.common.black,
                            fontWeight: 600,
                            fontSize: '14px',
                            minHeight: '32px',
                            height: 'auto',
                            borderRadius: '16px',
                            padding: '0 12px',
                            border: '1px solid #BABFD0',
                            display: 'flex',
                            flexDirection: 'row-reverse',
                            justifyContent: 'flex-end',
                            maxWidth: '100%',
                            textAlign: 'left',
                            alignItems: 'center',
                            whiteSpace: 'normal',
                            '& .MuiChip-label': {
                                paddingLeft: '4px',
                                whiteSpace: 'normal',
                                overflow: 'visible',
                                textOverflow: 'unset'
                            },
                            '& .MuiChip-deleteIcon': {
                                color: theme.palette.common.black
                            }
                        }}
                    />
                );
            })}
        </Stack>
    );
};

export const ComplianceRulesFormContainer = ({ onClose }: ComplianceRulesFormContainerProps) => {
    const { setValue, watch } = useFormContext();
    const [openCnaesDrawer, setOpenCnaesDrawer] = useState<{ open: boolean; index: number | null }>(
        { open: false, index: null }
    );
    const complianceRuleTypesValue = watch('complianceRuleTypes');
    const complianceRuleTypes: number[] = useMemo(
        () => complianceRuleTypesValue ?? [],
        [complianceRuleTypesValue]
    );
    const complianceRules: IComplianceRules[] = watch('complianceRules') ?? [];
    const { autoCompleteProps: economicActvCodeCompleteProps } = useEconomicActivityCodeListData();

    const complianceListOrder = useMemo(() => {
        const targetValue = 0;
        if (complianceRuleTypes?.length === 0) return complianceRulesTypesList;
        return complianceRuleTypes.includes(targetValue)
            ? complianceRulesTypesList.filter((x) => x.value === targetValue)
            : complianceRulesTypesList.filter((x) => x.value !== targetValue);
    }, [complianceRuleTypes]);

    const onRemoveItem = (option: IComplianceRules) => {
        setValue(
            'complianceRules',
            complianceRules.filter((x) => x.complianceRulesType !== option.complianceRulesType)
        );
        setValue(
            'complianceRuleTypes',
            complianceRuleTypes.filter((x) => x !== option.complianceRulesType)
        );
    };

    const scrModalitiesOptions = useMemo(
        () =>
            [
                { label: '0402 – Aquisição de Outros Bens', value: '0402' },
                { label: '0401 – Aquisição de Veículos', value: '0401' },
                { label: '1201 – Arrendamento Mercantil – Outros Bens', value: '1201' },
                { label: '1206 – Arrendamento Mercantil – Veículos', value: '1206' },
                { label: '1304 – Cartão de Crédito – À vista', value: '1304' },
                { label: '0210 – Cartão de Crédito – Parcelado', value: '0210' },
                { label: '0406 – Cartão de Crédito – Parcelado', value: '0406' },
                { label: '0204 – Cartão de Crédito – Rotativo', value: '0204' },
                { label: '0218 – Cartão de Crédito – Rotativo', value: '0218' },
                { label: '0101 – Cheque Especial', value: '0101' },
                { label: '0213 – Cheque Especial', value: '0213' },
                { label: '0214 – Cheque Especial', value: '0214' },
                { label: '0302 – Desconto de Cheques', value: '0302' },
                { label: '0299 – Crédito Pessoal – Não Consignado (Renegociação)', value: '0299' },
                { label: '0203 – Crédito Pessoal – Não Consignado', value: '0203' },
                { label: '0211 – Crédito Pessoal – Não Consignado', value: '0211' },
                {
                    label: '0202 – Crédito Pessoal – Consignado (INSS, privado, público)',
                    value: '0202'
                },
                { label: '0212 – Microcrédito (Consumo/Microempreendedor)', value: '0212' },
                { label: '0403 – Microcrédito (Consumo/Microempreendedor)', value: '0403' },
                { label: '1408 – Microcrédito (FGI/PEAC/Pronampe)', value: '1408' },
                { label: '0901 – Financiamento Imobiliário – SFH/SBPE', value: '0901' },
                { label: '0902 – Financiamento Imobiliário', value: '0902' },
                { label: '0903 – Financiamento Imobiliário', value: '0903' },
                { label: '0990 – Financiamento Imobiliário', value: '0990' },
                { label: '1202 – Financiamento Imobiliário (Outro)', value: '1202' }
            ].sort((a, b) => a.value.localeCompare(b.value)),
        []
    );

    const handleComplianceRuleTypesChange = ({ target: { value } }: any) => {
        const list = ((value ?? []) as string[])?.map((x) =>
            Number(getComplianceRuleByDescription(x))
        );
        setValue('complianceRuleTypes', list);
        const compliaceList = list?.map((option) => {
            const existing = complianceRules.find((x) => x.complianceRulesType === option);

            if (option === 11) {
                return {
                    complianceRulesActionType: existing?.complianceRulesActionType ?? null,
                    complianceRulesType: option,
                    scrModalitiesBlacklist: existing?.scrModalitiesBlacklist ?? null,
                    maximumAlowedValueSCR: existing?.maximumAlowedValueSCR ?? null
                };
            }
            if (option === 10) {
                return {
                    complianceRulesActionType: existing?.complianceRulesActionType ?? null,
                    complianceRulesType: option,
                    blacklistedCnaes: existing?.blacklistedCnaes ?? null
                };
            }
            return {
                complianceRulesActionType: existing?.complianceRulesActionType ?? null,
                complianceRulesType: option
            };
        });
        setValue('complianceRules', compliaceList);
    };

    const handleDeleteCnae = (index: number, cnae: string) => {
        const updatedCnaes = watch(`complianceRules.${index}.blacklistedCnaes`).filter(
            (item: any) => item !== cnae
        );
        setValue(`complianceRules.${index}.blacklistedCnaes`, updatedCnaes);
    };

    return (
        <Stack spacing={2}>
            <SelectFormField
                name="complianceRuleTypes"
                label="Regras de compliance"
                variant="outlined"
                multiple
                onChange={handleComplianceRuleTypesChange}
                options={complianceListOrder}
            />

            {complianceRules?.length > 0 && (
                <Typography fontSize="15px" fontWeight="bold">
                    Defina as ações para cada regra adicionada
                </Typography>
            )}

            {complianceRules?.map((option, index) => {
                const showActionField = ![0, 9].includes(option.complianceRulesType);
                const blacklistedCnaes = watch(`complianceRules.${index}.blacklistedCnaes`) ?? [];
                return (
                    <Stack
                        key={option.complianceRulesType}
                        spacing={2}
                        sx={{
                            border: '1px solid #BABFD0',
                            borderRadius: '8px',
                            padding: '7px'
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            pb={0.5}
                            justifyContent="space-between"
                            borderBottom={
                                showActionField ? `1px solid ${theme.palette.grey[200]}` : 'none'
                            }
                        >
                            <Typography fontSize="17px" fontWeight="bold">
                                {getComplianceRuleDescription(option.complianceRulesType)}
                            </Typography>
                            <IconButton onClick={() => onRemoveItem(option)}>
                                <DeleteIcon />
                            </IconButton>
                        </Stack>

                        {showActionField && (
                            <Stack mt={2}>
                                <SelectFormField
                                    name={`complianceRules.${index}.complianceRulesActionType`}
                                    label="Ação"
                                    fullWidth
                                    options={[
                                        { label: 'Aprovação manual', value: 1 },
                                        { label: 'Rejeitar', value: 0 }
                                    ]}
                                />
                            </Stack>
                        )}

                        {option.complianceRulesType === 6 && (
                            <TextFormField
                                name="defaultComplianceAnalysisGroup"
                                label="Grupo de análise compliance"
                                type="number"
                                variant="outlined"
                                fullWidth
                                required
                            />
                        )}

                        {option.complianceRulesType === 11 && (
                            <Fragment>
                                <Typography fontSize="15px" fontWeight="bold">
                                    Defina as modalidades que deseja bloquear
                                </Typography>
                                <SelectFormField
                                    name={`complianceRules.${index}.scrModalitiesBlacklist`}
                                    label="Selecionar modalidades"
                                    multiple
                                    options={scrModalitiesOptions}
                                    fullWidth
                                />
                                <Typography fontSize="15px" fontWeight="bold">
                                    Valor mínimo para ativar o bloqueio
                                </Typography>
                                <TextFormField
                                    name={`complianceRules.${index}.maximumAlowedValueSCR`}
                                    label="Valor"
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Fragment>
                        )}

                        {option.complianceRulesType === 10 && (
                            <Fragment>
                                <Typography fontSize="15px" fontWeight="bold">
                                    Defina quais CNAEs deseja bloquear
                                </Typography>
                                <AutocompleteField
                                    name={`complianceRules.${index}.blacklistedCnaes`}
                                    showEndIcon
                                    label={
                                        blacklistedCnaes?.length > 0
                                            ? `${blacklistedCnaes?.length} CNAE${
                                                  blacklistedCnaes?.length > 1 ? 's' : ''
                                              } selecionado${
                                                  blacklistedCnaes?.length > 1 ? 's' : ''
                                              }`
                                            : 'Selecione CNAEs'
                                    }
                                    closeAutocompleteOnSelect
                                    disabledTags
                                    multiple
                                    {...economicActvCodeCompleteProps}
                                    value={blacklistedCnaes?.map((code: string) => {
                                        const found =
                                            economicActvCodeCompleteProps.listOptions.find(
                                                (opt: any) => opt?.code === code
                                            );
                                        return found || { label: code, value: code, code };
                                    })}
                                    onChange={(_, selected) => {
                                        setValue(
                                            `complianceRules.${index}.blacklistedCnaes`,
                                            Array.isArray(selected)
                                                ? selected?.map((opt: any) => opt.code)
                                                : []
                                        );
                                    }}
                                    loading={economicActvCodeCompleteProps.loading}
                                />
                                {blacklistedCnaes?.length > 0 && (
                                    <Fragment>
                                        <Typography
                                            fontFamily="Lato"
                                            fontWeight={600}
                                            fontSize="18px"
                                            lineHeight="120%"
                                            letterSpacing="-0.2px"
                                        >
                                            {`Você selecionou ${blacklistedCnaes?.length} CNAE${
                                                blacklistedCnaes?.length === 1 ? '' : 's'
                                            }`}
                                        </Typography>
                                        <CnaeChipsGrid
                                            cnaes={blacklistedCnaes}
                                            listOptions={economicActvCodeCompleteProps.listOptions}
                                            onDelete={(cnae) => handleDeleteCnae(index, cnae)}
                                            max={6}
                                        />
                                        {blacklistedCnaes?.length > 5 && (
                                            <Stack
                                                sx={{
                                                    gridColumn: '1 / -1',
                                                    display: 'flex',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Button
                                                    variant="text"
                                                    onClick={() =>
                                                        setOpenCnaesDrawer({
                                                            open: true,
                                                            index
                                                        })
                                                    }
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontFamily: 'Lato',
                                                        fontWeight: 700,
                                                        fontSize: '16px',
                                                        lineHeight: '120%',
                                                        letterSpacing: '-0.2px',
                                                        textAlign: 'center',
                                                        marginBottom: '16px !important',
                                                        color: theme.palette.common.black
                                                    }}
                                                >
                                                    <RightIcon
                                                        htmlColor={theme.palette.common.black}
                                                        sx={{
                                                            marginRight: '4px',
                                                            fontSize: '16px',
                                                            height: '24px',
                                                            width: '24px'
                                                        }}
                                                    />
                                                    {`Exibir todos os ${blacklistedCnaes?.length} CNAEs`}
                                                </Button>
                                            </Stack>
                                        )}
                                    </Fragment>
                                )}
                                <Drawer
                                    anchor="right"
                                    open={openCnaesDrawer.open && openCnaesDrawer.index === index}
                                    onClose={() => setOpenCnaesDrawer({ open: false, index: null })}
                                >
                                    <Stack spacing={2}>
                                        <Typography fontSize="22px" fontWeight="bold" mb={1}>
                                            CNAEs bloqueados
                                        </Typography>
                                        <Typography fontSize="15px" color="text.secondary">
                                            Confira as informações antes de editar os CNAEs
                                            selecionados
                                        </Typography>
                                        <Typography
                                            fontFamily="Lato"
                                            fontWeight={600}
                                            fontSize="18px"
                                            lineHeight="120%"
                                            letterSpacing="-0.2px"
                                        >
                                            {`Você selecionou ${blacklistedCnaes?.length} CNAEs`}
                                        </Typography>
                                        <CnaeChipsGrid
                                            cnaes={blacklistedCnaes}
                                            listOptions={economicActvCodeCompleteProps.listOptions}
                                            onDelete={(cnae) => handleDeleteCnae(index, cnae)}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={() =>
                                                setOpenCnaesDrawer({ open: false, index: null })
                                            }
                                            sx={{
                                                mt: 2,
                                                fontFamily: 'Lato',
                                                fontWeight: 700,
                                                fontSize: '16px',
                                                lineHeight: '120%',
                                                letterSpacing: '-0.2px',
                                                color: theme.palette.common.white
                                            }}
                                            startIcon={
                                                <CloseIcon htmlColor={theme.palette.common.white} />
                                            }
                                        >
                                            Fechar
                                        </Button>
                                    </Stack>
                                </Drawer>
                            </Fragment>
                        )}
                    </Stack>
                );
            })}

            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    startIcon={<CloseIcon htmlColor={theme.palette.primary.main} sx={iconSx} />}
                >
                    Fechar
                </Button>
                <Button
                    startIcon={<SaveIcon htmlColor={theme.palette.common.white} sx={iconSx} />}
                    variant="contained"
                    size="medium"
                    type="submit"
                >
                    Salvar
                </Button>
            </Stack>
        </Stack>
    );
};
